// Historia 148 — las 7 procedencias documentales de una fabricación y sus 35 permisos.
//
// Módulo HOJA, sin imports: lo consumen el botón (prevención, S16/S17) y el modal (títulos de
// grupo, S50), y ninguno de los dos debe arrastrar nada por usarlo.
//
// ⚠️ El conjunto es CERRADO (RN3) y el orden es el del contrato congelado. Jupiter NO reordena
// (RN13/S50): esta lista existe para traducir el título de un grupo y para construir el código de
// permiso, nunca para decidir qué grupos se pintan — eso llega ya filtrado y ya ordenado de Sun.

/**
 * Los 9 tipos de procedencia del contrato N1 tras la enmienda E1, en el orden en que Sun los
 * emite. Los dos nuevos van SIEMPRE al final, nunca intercalados entre los 7 originales
 * (RN13/S82): el orden de este array es el orden de pintado.
 */
const DOCUMENT_SOURCE_TYPES = [
  'FABRICATION_ORDER',
  'PART_REFERENCE',
  'WORKFLOW',
  'JOB',
  'TASK',
  'RAW_MATERIAL_REFERENCE',
  'TOOL_REFERENCE',
  'FEATURE_GROUP',
  'FEATURE_TASK'
]

/**
 * Los dos tipos que la enmienda E1 añade. Sus registros salen PLEGADOS (RN39/S94): una feature
 * alcanzada desde cada tarea multiplica las cabeceras, y con todo abierto la columna se vuelve
 * inmanejable. Los 7 originales no cambian — S50 sigue verde.
 */
const COLLAPSED_BY_DEFAULT_SOURCE_TYPES = ['FEATURE_GROUP', 'FEATURE_TASK']

/** Las 5 acciones documentales. `VIEW` es la puerta: sin ella el grupo no existe (RN18). */
const DOCUMENT_ACTIONS = ['VIEW', 'DOWNLOAD', 'ADD', 'EDIT', 'DELETE']

/**
 * Título traducible de cada grupo (S50). La clave es el literal inglés, según la convención del
 * repositorio; todas menos `Raw materials` ya existen en los 8 ficheros de idioma.
 */
const DOCUMENT_SOURCE_TITLE_KEYS = {
  FABRICATION_ORDER: 'Fabrication order',
  PART_REFERENCE: 'Part reference',
  WORKFLOW: 'Process',
  JOB: 'Operation',
  TASK: 'Tasks',
  RAW_MATERIAL_REFERENCE: 'Raw materials',
  TOOL_REFERENCE: 'Tools',
  FEATURE_GROUP: 'Feature groups',
  FEATURE_TASK: 'Features'
}

/**
 * RN37/S93 — la RUTA de un registro, compuesta EN EL CLIENTE a partir de los tramos que manda
 * Sun. El contrato transporta los tramos, nunca una cadena ya compuesta: el separador, el orden
 * de lectura y qué se pinta de cada tramo los decide el puesto.
 *
 * Los tramos llegan del ancestro más lejano al más cercano, así que se leen tal cual. De cada uno
 * se pinta su NOMBRE cuando aporta algo más que el código (un grupo de feature se reconoce por
 * «FG-3 Dimensional», no por «FG-3»); con un solo tramo no hay separador que sobre (D20/S80).
 * @param {Array} segments `sourcePath` de una entrada o `path` de un destino
 * @returns {string} Cadena vacía cuando no hay ruta — los 7 tipos originales
 */
const composeSourcePath = (segments = []) => segments
  .map((segment) => segment?.name || segment?.code || '')
  .filter((label) => label !== '')
  .join(' \u203a ')

/**
 * RN41.8/S97 — REUNIR las rutas de un mismo destino. `targets` llega SIN deduplicar: la misma
 * feature alcanzada desde dos tareas viaja dos veces, con dos rutas. Elegir una u otra produce
 * exactamente el mismo alta (el documento cuelga de la feature, no de la tarea — D19), así que
 * ofrecer dos destinos idénticos solo invita a equivocarse. La deduplicación es PRESENTACIÓN del
 * puesto: el contrato no deduplica y no debe hacerlo.
 *
 * Con rutas de la misma longitud se reúnen tramo a tramo, para que la diferencia quede a la
 * vista: `[T-01, FG-3]` y `[T-02, FG-3]` dan «T-01, T-02 › FG-3 Dimensional», no dos líneas
 * casi iguales. Con longitudes distintas se listan enteras, separadas por `|`, antes que
 * inventar una fusión que mentiría sobre la jerarquía.
 * @param {Array<Array>} paths Las rutas (`path`) de los destinos que comparten `referenceId`
 * @returns {string}
 */
const mergeTargetPaths = (paths = []) => {
  const composed = paths.map((path) => composeSourcePath(path)).filter((label) => label !== '')
  const distinct = [...new Set(composed)]
  if (distinct.length <= 1) {
    return distinct[0] || ''
  }
  const lengths = [...new Set(paths.map((path) => path.length))]
  if (lengths.length > 1) {
    return distinct.join(' | ')
  }
  return Array.from({ length: lengths[0] }, (unused, index) => {
    const labels = paths.map((path) => path[index]?.name || path[index]?.code || '')
    return [...new Set(labels.filter((label) => label !== ''))].join(', ')
  })
    .filter((label) => label !== '')
    .join(' \u203a ')
}

/**
 * RN41/S97 — los destinos de alta de un grupo, ya deduplicados por identidad y con sus rutas
 * reunidas. Es lo único que el selector debe ofrecer.
 * @param {Object} group Un grupo del contrato N1
 * @returns {Array<{referenceId: string, code: string, name: string, path: string}>}
 */
const contributionTargetsOf = (group) => {
  const byIdentity = []
  ;(group?.targets || []).forEach((target) => {
    const existing = byIdentity.find((candidate) => candidate.referenceId === target.referenceId)
    if (existing) {
      existing.paths.push(target.path || [])
      return
    }
    byIdentity.push({
      referenceId: target.referenceId,
      code: target.code,
      name: target.name,
      paths: [target.path || []]
    })
  })
  return byIdentity.map(({ paths, ...target }) => ({ ...target, path: mergeTargetPaths(paths) }))
}

/** Los dos tipos cuya documentación vive en el CATÁLOGO DE CALIDAD COMPARTIDO (D19/S98). */
const SHARED_QUALITY_CATALOGUE_TYPES = ['FEATURE_GROUP', 'FEATURE_TASK']

/**
 * Código del permiso de MIC para una procedencia y una acción: `DOCUMENTS_<TIPO>_<ACCIÓN>`.
 * Espejo exacto de `DocumentPermissionCode.forAction` en Sun — los 35 ya existen en base de datos
 * (D13, sin migración).
 * @param {string} sourceType Uno de `DOCUMENT_SOURCE_TYPES`
 * @param {string} action Una de `DOCUMENT_ACTIONS`
 * @returns {string}
 */
const documentPermissionCode = (sourceType, action) => 'DOCUMENTS_' + sourceType + '_' + action

/** Los 35 códigos, para quien necesite el conjunto entero. */
const ALL_DOCUMENT_PERMISSION_CODES = DOCUMENT_SOURCE_TYPES.flatMap(
  (sourceType) => DOCUMENT_ACTIONS.map((action) => documentPermissionCode(sourceType, action))
)

/**
 * RN19 / S16 / S17: el acceso solo se ofrece si el perfil puede ver documentación de ALGUNA de las
 * 7 procedencias. Es prevención de UX (D3), no la garantía — el servidor vuelve a filtrar (S38).
 * @param {(permissionCode: string) => boolean} isUserAuthorized El evaluador de `useAuthorization`
 * @returns {boolean}
 */
const hasAnyDocumentViewPermission = (isUserAuthorized) =>
  DOCUMENT_SOURCE_TYPES.some((sourceType) => isUserAuthorized(documentPermissionCode(sourceType, 'VIEW')))

export {
  DOCUMENT_SOURCE_TYPES,
  COLLAPSED_BY_DEFAULT_SOURCE_TYPES,
  SHARED_QUALITY_CATALOGUE_TYPES,
  composeSourcePath,
  mergeTargetPaths,
  contributionTargetsOf,
  DOCUMENT_ACTIONS,
  DOCUMENT_SOURCE_TITLE_KEYS,
  ALL_DOCUMENT_PERMISSION_CODES,
  documentPermissionCode,
  hasAnyDocumentViewPermission
}
